import SkTechApiClient from "./base/SkTechApiClient";
import dayjs, { Dayjs } from 'dayjs';
import { UploadFile } from "antd";
import { RcFile } from "antd/es/upload";
import { JobTitle } from "../models/JobTitle";
import { HistoryItem } from "../models/HistoryItem";
import { Employee } from "../models/Employee";
import { Experience } from "../models/Experience";

export default class EmployeesApiClient extends SkTechApiClient {

  //#region Account Registration
  public accountRegister = async (email: string,
    password: string,
    firstName: string,
    lastName: string,
    companyId: number,
    role: string = "user"): Promise<any> => {
    return await this.Post('/api/Account/register', {
      "email": email,
      "password": password,
      "FirstName": firstName,
      "LastName": lastName,
      "companyId": companyId,
      "role": role
    })
      .then((response) => {
        if (response.status === 200) {
          return response.data;
        }
        return false;
      })
      .catch(function (error) {
        console.log(error);
        return false;
      });
  }
  //#endregion

  
  //#region Job Titles
  public getJobTitles = async (): Promise<JobTitle[]> => {
    return await this.Get('/api/JobTitle').then((response) => {
      if (response.status === 200) {
        return response.data;
      }
      return [];
    })
      .catch(function (error) {
        console.log(error);
        return [];
      });
  }

  public addJobTitle = async (title: string, description: string): Promise<any> => {
    return await this.Post(`/api/JobTitle`, {
      "title": title,
      "description": description
    }).catch(error => {
      throw new Error(error.response.data)
    })
  }
  //#endregion


  //#region Email
  public changeEmail = async (newEmail: string): Promise<boolean> => {
    return await this.Post(`/api/Account/ChangeEmail?newEmail=${newEmail}`)
      .then((response) => {
        if (response.status === 200) {
          return true;
        }
        return false;
      })
      .catch(function (error) {
        console.log(error);
        return false;
      });
  }

  public EmailCheck = async (email: string): Promise<boolean> => {
    return await this.Get(`/api/CompanyUsers/CheckEmail?email=${email}`)
      .then(function (response) {
        if (response.status === 200) {
          return response.data;
        }
        return false;
      })
      .catch(function (error) {
        console.log(error);
        return false;
      });
  }
  //#endregion


  //#region Email Confirmation
  public ConfirmEmail = async (userId: string, code: string): Promise<boolean> => {
    return await this.Get(`/api/Account/ConfirmEmail?userId=${userId}&code=${code}`)
      .then(function (response) {
        if (response.status === 200) {
          return response.data;
        }
        return false;
      })

  }

  public ConfirmChangedEmail = async (oldEmail: string, newEmail: string, code: string): Promise<boolean> => {
    return await this.Get(`/api/Account/ConfirmChangedEmail?oldEmail=${oldEmail}&newEmail=${newEmail}&code=${code}`)
      .then(function (response) {
        if (response.status === 200) {
          return response.data;
        }
        return false;
      })

  }
  //#endregion


  //#region User
  public getUsers = async (): Promise<Employee[]> => {
    return await this.Get('/api/CompanyUsers')
      .then((response) => {
        if (response.status === 200) {
          if (Array.isArray(response.data)) {
            return response.data.map((el: {
              birthDay: string | number | Date | Dayjs | null | undefined;
              roles: { id: number; name: string }[] | undefined;
              userRoles: { userId: number; roleId: number; role: { id: number; title: string } }[];
            }) => {
              const roles = el.userRoles ? el.userRoles.map(ur => ({ id: ur.roleId, name: ur.role.title })) : [];

              return {
                ...el,
                roles: roles,
                birthDay: el.birthDay ? dayjs(el.birthDay) : null,
              };
            });
          } else {
            console.error('Expected an array but got:', response.data);
            return [];
          }
        } else if (response.status === 402) {
          return [];
        }
        return [];
      })
      .catch((error) => {
        console.log("getUsers error: ", error);
        return [];
      });
  }

  public getUserById = async (id: string): Promise<Employee> => {
    return await this.Get(`/api/CompanyUsers/${id}`)
      .then((response) => {
        if (response.status === 200) {
          const user = response.data;
          const roles = user.userRoles
            ? user.userRoles.map((ur: { roleId: number; role: { id: number; title: string } }) => {
              return {
                id: ur.roleId,
                name: ur.role.title,
              };
            })
            : [];

          const mappedUser = {
            ...user,
            roles,
            birthDay: user.birthDay ? dayjs(user.birthDay) : null,
          };
          return mappedUser;
        }
        throw new Error('User not found');
      })
      .catch((error) => {
        console.error("Detailed error in getUserById:", error);
        throw error;
      });
  };

  public editUser = async (user: Employee): Promise<any> => {
    const payload: any = {
      ...user,
      userRoles: user.userRoles || []
    };

    if (user.birthDay) {
      payload.birthDay = dayjs(user.birthDay).format('YYYY-MM-DD');
    }

    const cleanedPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, value]) => value !== undefined)
    );

    return await this.Patch(`/api/CompanyUsers/${user.id}`, cleanedPayload)
      .then((response) => {
        if (response.status === 200) {
          return response.data;
        }
        return false;
      })
      .catch(function (error) {
        console.error("Error editing user:", error);
        return false;
      });
  };

  public getHistoryByUser = async (id: string): Promise<HistoryItem[]> => {
    return await this.Get(`/api/CompanyUsers/GetHistoryByUser?userId=${id}`)
      .then((response) => {
        if (response.status === 200) {
          return response.data;
        }
        return false;
      })
      .catch(function (error) {
        console.log(error);
        return false;
      });
  }

  public deleteUser = async (userId: string): Promise<void> => {
    return await this.Delete(`/api/CompanyUsers/${userId}`)
      .then(response => {
        if (response.status !== 200) {
          throw new Error("Failed to delete user");
        }
      })
      .catch(error => {
        console.error("Error deleting user:", error);
        throw error;
      });
  };

  public deleteUserPermanently = async (userId: string): Promise<void> => {
    return await this.Delete(`/api/CompanyUsers/permanently/${userId}`)
      .then(response => {
        if (response.status !== 200) {
          throw new Error("Failed to delete user");
        }
      })
      .catch(error => {
        console.error("Error deleting user:", error);
        throw error;
      });
  };

  public inviteUser = async (payload: {
    userEmail: string;
    userRoles: number[];
    chiefId: string;
    firstName: string;
    lastName: string;
    personalEmails: string[];
    jobTitleId: string;
  }): Promise<boolean> => {
    return await this.Post('/api/Account/Invite', payload)
      .then(res => res.status === 200)
      .catch(err => { throw new Error(err.response?.data ?? 'Invite failed'); });
  };
  //#endregion


  //#region Expirience
  public addExperience = async (company: string, project: string, projectId: string, position: string, description: string, startDate: any, endDate: any) => {
    return await this.Post(`/api/CompanyUsers/AddExperience`, {
      "Company": company,
      "Project": project,
      "ProjectId": projectId,
      "Position": position,
      "Description": description,
      "StartDate": startDate,
      "EndDate": endDate
    })
      .then(function (response) {
        if (response.status === 200) {
          return true;
        }
        return false;
      })
      .catch(function (error) {
        console.log(error);
        return false;
      });
  }

  public editExperience = async (prevItem: Experience, company: string, project: string, projectId: string, position: string, description: string, startDate: any, endDate: any) => {
    return await this.Post(`/api/CompanyUsers/EditExperience`, {
      "prevItem": prevItem,
      "Company": company,
      "Project": project,
      "ProjectId": projectId,
      "Position": position,
      "Description": description,
      "StartDate": startDate,
      "EndDate": endDate
    })
      .then(function (response) {
        if (response.status === 200) {
          return true;
        }
        return false;
      })
      .catch(function (error) {
        console.log(error);
        return false;
      });
  }

  public removeExperience = async (company: string, project: string, position: string, description: string, startDate: any, endDate: any) => {
    return await this.Post(`/api/CompanyUsers/RemoveExperience`, {
      "Company": company,
      "Project": project,
      "Position": position,
      "Description": description,
      "StartDate": startDate,
      "EndDate": endDate
    })
      .then(function (response) {
        if (response.status === 200) {
          return true;
        }
        return false;
      })
      .catch(function (error) {
        console.log(error);
        return false;
      });
  }
  //#endregion


  //#region Password Restoration
  public changePassword = async (oldPassword: string, newPassword: string): Promise<boolean> => {
    return await this.Post(`/api/Account/ChangePassword`, {
      oldPassword,
      newPassword
    })
      .then((response) => {
        if (response.status === 200) {
          return true;
        }
        return false;
      })
      .catch(function (error) {
        console.log(error);
        return false;
      });
  }

  public forgetPassword = async (email: string, language: string): Promise<void> => {
    return await this.Post(`/api/Account/forgot-password`, {
      "email": email,
      "language": language
    })
      .then(response => {
        if (response.status !== 200) {
          throw new Error("Failed to delete user");
        }
      })
      .catch(error => {
        console.error("Error deleting user:", error);
        throw error;
      });
  };

  public restorePassword = async (email: string, token: string, newPassword: string): Promise<void> => {
    return await this.Post(`/api/Account/reset-password`, {
      "email": email,
      "token": token,
      "newPassword": newPassword
    })
      .then(response => {
        if (response.status !== 200) {
          throw new Error("Failed to delete user");
        }
      })
      .catch(error => {
        console.error("Error deleting user:", error);
        throw error;
      });
  };
  //#endregion


  //#region Resume
  /**
   * Downloads a server-rendered resume PDF.
   *
   * The document is generated with QuestPDF on the backend, so what arrives here is a real
   * vector PDF (~150 KB, selectable text) instead of the multi-megabyte html2canvas raster
   * this used to produce in the browser.
   *
   * @param userId   target employee id
   * @param template "cv" - print-first CV, "profile" - copy of the profile page
   * @param lang     UI language; the server falls back to the company language, then English
   */
  public exportResumePdf = async (
    userId: string,
    template: "cv" | "profile",
    lang: string
  ): Promise<{ blob: Blob; contentDisposition?: string }> => {
    const response = await this.Get(
      `/api/CompanyUsers/${userId}/Resume?format=pdf&template=${template}&lang=${encodeURIComponent(lang)}`,
      'blob'
    );

    return {
      blob: response.data as Blob,
      contentDisposition: response.headers?.["content-disposition"],
    };
  };
  //#endregion

  //#region Photo
  public photoUpload = async (file: UploadFile, userId: string): Promise<any> => {
    const formData = new FormData();
    formData.append("file", file.originFileObj as RcFile);
    return await this.Post(`/api/File/UploadUserPhoto?id=${userId}`, formData)
      .then((response) => {
        if (response.status === 200) {
          return response.data;
        }
        return false;
      })
      .catch(function (error) {
        console.log(error);
        return false;
      });
  };
  //#endregion
}